import { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_keyless-vending-1/artifacts/9rpednbz_keyless-vending-logo.png';

const NAV_LINKS = [
  { label: 'What We Do', href: '#what-we-do' },
  { label: 'Machines', href: '#machines' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Service Area', href: '#service-area' },
  { label: 'Contact', href: '#contact' },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      data-testid="top-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-kv-black/90 backdrop-blur-xl border-b border-white/10 shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-20">
        {/* Logo */}
        <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          <img
            src={LOGO_URL}
            alt="Keyless Vending"
            className="h-12 sm:h-14 w-auto object-contain"
          />
        </a>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.href.replace('#', '')}`}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-sm text-white/70 hover:text-white transition-colors font-sans"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-get-a-contract-button"
            onClick={(e) => handleNavClick(e, '#contact')}
            className="rounded-full bg-white/10 text-white hover:bg-white/15 border border-white/15 backdrop-blur-md px-5 py-2 text-sm font-medium transition-colors"
          >
            Get a Contract
          </a>
        </div>

        {/* Mobile Menu */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-kv-black border-white/10 w-72">
              <SheetTitle className="text-white text-lg font-display">Menu</SheetTitle>
              <div className="flex flex-col gap-4 mt-8">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-white/70 hover:text-white text-base py-2 transition-colors font-sans"
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href="#contact"
                  onClick={(e) => handleNavClick(e, '#contact')}
                  className="mt-4 rounded-full bg-kv-gold text-kv-black hover:bg-kv-gold-2 px-6 py-3 text-sm font-semibold text-center transition-colors"
                >
                  Get a Contract
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
